/**
 * Unidades de medida: lo que se despacha contra lo que se cobra.
 *
 * El contrato no cobra todo por metro cúbico. Hay partidas en tonelada
 * (morteros, cementos), por saco, por kilo y por unidad. La guía, en cambio,
 * anota lo que el operador cuenta en planta: sacos, kilos, metros cúbicos.
 *
 * Mientras la app asumió que toda cantidad era m³, una guía de 80 sacos de
 * mortero T-100 se cobraba como 80 toneladas contra la partida 13.33
 * (26,5 UF/TON): 2.120 UF en vez de 53. Cuarenta veces de más, sin que nada
 * lo advirtiera.
 *
 * Aquí se convierte de la unidad en que se despachó a la unidad en que se
 * cobra. Cuando la conversión no es posible —o falta el dato para hacerla—
 * **no se inventa un número**: se devuelve el motivo y el estado de pago
 * deja la guía afuera con una advertencia. Un cobro equivocado es peor que
 * un cobro que falta, porque el que falta se nota.
 */

import type { ItemContrato, UnidadItem } from './tipos';

/** En qué unidad se anotó la cantidad de la guía. */
export type UnidadCantidad = 'M3' | 'SACO' | 'KG' | 'TON' | 'UND';

export const UNIDADES_CANTIDAD: UnidadCantidad[] = ['M3', 'SACO', 'KG', 'TON', 'UND'];

export const NOMBRE_UNIDAD: Record<UnidadCantidad, string> = {
  M3: 'm³',
  SACO: 'sacos',
  KG: 'kg',
  TON: 'ton',
  UND: 'unidades',
};

/** La magnitud física en la que cobra una partida del contrato. */
type Magnitud = 'M3' | 'TON' | 'KG' | 'SACO' | 'UND' | null;

/**
 * Qué mide realmente cada unidad de contrato.
 *
 * `UND`, `UF/DIA`, `GL` y `B` son todas cuentas: cuántas unidades, cuántos
 * días de apertura de planta, cuántos traslados. No hay conversión que hacer,
 * sólo un número. Se agrupan porque tratarlas distinto no aporta nada y sí
 * dejaba fuera cobros reales — la apertura de planta (4.4) viene por guía en
 * la hoja PRODUCTO ESP y se facturó en el EP 59.
 *
 * `MES` sí queda fuera: los gastos generales entran como cuota del período,
 * no como despacho.
 */
function magnitudDe(unidad: UnidadItem): Magnitud {
  switch (unidad) {
    case 'M3':
    case 'UF/M3':
      return 'M3';
    case 'TON':
      return 'TON';
    case 'UF/KG':
      return 'KG';
    case 'UF/SACO':
      return 'SACO';
    case 'UND':
    case 'UF/DIA':
    case 'GL':
    case 'B':
      return 'UND';
    default:
      return null; // MES
  }
}

/**
 * Kilos por saco, leídos de la descripción de la partida.
 *
 * El contrato lo dice con todas sus letras —«Mortero T-100 saco 25 kilos»—
 * así que el dato se toma de ahí en vez de fijarlo en el código: si mañana
 * aparece un formato de 42,5 kg, basta con que la partida lo diga.
 *
 * Devuelve `null` si la descripción no declara el peso. En ese caso no se
 * adivina.
 */
export function kgPorSaco(descripcion: string): number | null {
  const d = descripcion.toLowerCase();
  if (!d.includes('saco')) return null;

  // «saco 25 kilos», «saco de 42,5 kg», «sacos de 25 k»
  const m = d.match(/sacos?\s*(?:de\s*)?(\d+(?:[.,]\d+)?)\s*(?:kilos?|kgs?|k\b)/);
  if (!m?.[1]) return null;

  const kg = Number(m[1].replace(',', '.'));
  return Number.isFinite(kg) && kg > 0 ? kg : null;
}

export type Conversion =
  { ok: true; cantidad: number; unidadCobro: UnidadItem } | { ok: false; motivo: string };

/** Factores entre unidades de masa. Los sacos dependen de la partida. */
const KG_POR_TON = 1000;

/**
 * Lleva la cantidad de la guía a la unidad en que cobra la partida.
 *
 * `cantidad` va en `unidadGuia`; el resultado va en la unidad del `item`.
 */
export function convertirParaCobro(
  cantidad: number,
  unidadGuia: UnidadCantidad,
  item: Pick<ItemContrato, 'codigo' | 'descripcion' | 'unidad'>,
): Conversion {
  if (!Number.isFinite(cantidad)) {
    return { ok: false, motivo: 'La cantidad no es un número.' };
  }

  const destino = magnitudDe(item.unidad);
  if (destino === null) {
    return {
      ok: false,
      motivo:
        `La partida ${item.codigo} se cobra por ${item.unidad}, que no se despacha en guías. ` +
        'Va como cuota o ajuste manual del estado de pago.',
    };
  }

  const noCalza = (): Conversion => ({
    ok: false,
    motivo:
      `La guía está en ${NOMBRE_UNIDAD[unidadGuia]} y la partida ${item.codigo} ` +
      `se cobra en ${item.unidad}. No hay forma de convertir una en otra.`,
  });

  // Sacos a masa: hace falta saber cuánto pesa el saco, y lo dice la partida.
  const sacoAKg = (): number | null => kgPorSaco(item.descripcion);
  const faltaPeso = (): Conversion => ({
    ok: false,
    motivo:
      `La partida ${item.codigo} no declara cuántos kilos tiene el saco, ` +
      'así que no se puede pasar a ' +
      `${item.unidad}. Corrige la descripción de la partida (por ejemplo «saco 25 kilos»).`,
  });

  let convertida: number | null = null;

  switch (destino) {
    case 'M3':
      if (unidadGuia === 'M3') convertida = cantidad;
      break;

    case 'TON':
      if (unidadGuia === 'TON') convertida = cantidad;
      else if (unidadGuia === 'KG') convertida = cantidad / KG_POR_TON;
      else if (unidadGuia === 'SACO') {
        const kg = sacoAKg();
        if (kg === null) return faltaPeso();
        convertida = (cantidad * kg) / KG_POR_TON;
      }
      break;

    case 'KG':
      if (unidadGuia === 'KG') convertida = cantidad;
      else if (unidadGuia === 'TON') convertida = cantidad * KG_POR_TON;
      else if (unidadGuia === 'SACO') {
        const kg = sacoAKg();
        if (kg === null) return faltaPeso();
        convertida = cantidad * kg;
      }
      break;

    case 'SACO':
      // Se cobra por saco: lo que importa es cuántos, no cuánto pesan.
      if (unidadGuia === 'SACO' || unidadGuia === 'UND') convertida = cantidad;
      break;

    case 'UND':
      if (unidadGuia === 'UND' || unidadGuia === 'SACO') convertida = cantidad;
      break;
  }

  if (convertida === null) return noCalza();
  return { ok: true, cantidad: convertida, unidadCobro: item.unidad };
}

/** ¿Esta partida se cobra en la misma unidad en que se despachó? */
export function unidadSugerida(item: Pick<ItemContrato, 'unidad' | 'descripcion'>): UnidadCantidad {
  switch (magnitudDe(item.unidad)) {
    case 'TON':
      // Si la partida habla de sacos, lo natural es que el operador cuente sacos.
      return kgPorSaco(item.descripcion) !== null ? 'SACO' : 'TON';
    case 'KG':
      return 'KG';
    case 'SACO':
      return 'SACO';
    case 'UND':
      return 'UND';
    default:
      return 'M3';
  }
}
